import React from 'react';
import { triggerToolbarGetCurrentPatient } from 'store/action/toolbar/getCurrentPatient';
import { useAppDispatch, useAppState } from 'store/configureStore';
import theme from 'styles/theme';
import { Feature, RenderOn } from '@econsult/econsult-ui/dist/components/FlagControlledFeature/index';
import Button from 'components/Button';
import { isFetching, isToolbarPatient, isGetPatientFail } from 'model/toolbar/patient';
import { STRINGS } from 'config';
import * as Style from './style';
import IsUsedWithToolbar from './Enabled';

const CurrentPatient: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    featureFlags,
    toolbar: { patient },
    app: { roles },
  } = useAppState();

  const DisplayPatient = (() => {
    if (!patient) {
      if (featureFlags.SUMMARISER_EMIS_INTEGRATION_ENABLED && roles.includes('TOOLBAR')) {
        triggerToolbarGetCurrentPatient(dispatch);
      }
      return <></>;
    }

    if (isFetching(patient)) {
      return <span data-automation="CurrentPatientLoading">Getting current open patient record…</span>;
    }
    if (isToolbarPatient(patient)) {
      return (
        <Style.PatientRow data-automation="CurrentPatientRow">
          <span>
            <Style.Label>ID</Style.Label> {patient.patientId}
          </span>
          <span>
            <Style.Label>Name</Style.Label> {patient.firstName.charAt(0)} {patient.surname}
          </span>
          <span>
            <Style.Label>DOB</Style.Label> {patient.dateOfBirth}
          </span>
        </Style.PatientRow>
      );
    }

    if (isGetPatientFail(patient)) {
      const errorStates: Record<number, JSX.Element> = {
        401: <>You are not logged in to the clinical system.</>,
        403: <>You do not have the correct permissions in the clinical system.</>,
        404: <>No patient record is open in the clinical system.</>,
      };

      return errorStates[patient.status] ?? <>Get current patient failed.</>;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const exhaustiveCheck: never = patient;
    return <></>;
  })();

  return (
    <Feature flags={[STRINGS.CUSTOMISATION.FEATURE_FLAGS.SUMMARISER_EMIS_INTEGRATION_ENABLED]}>
      <RenderOn>
        <IsUsedWithToolbar>
          <Style.Container data-automation="CurrentPatientContainer">
            <Style.Title data-automation="CurrentPatientLabel">Patient:</Style.Title>
            <Style.FlexRow>
              <Style.InfoSection>
                {DisplayPatient}
              </Style.InfoSection>
              <Button
                data-automation="CurrentPatientReloadButton"
                id="trigger-get-patient"
                onClick={() => triggerToolbarGetCurrentPatient(dispatch)}
                state="primary"
                disabled={isFetching(patient)}
                buttonText="Reload patient record"
                style={{ width: '200px', color: `${theme.colorPrimaryPrimary100} !important` }}
              />
            </Style.FlexRow>
          </Style.Container>
        </IsUsedWithToolbar>
      </RenderOn>
    </Feature>
  );
};

export default CurrentPatient;
